import { API_BASE } from "../../lib/api.ts";

/**
 * Client for the enrollment approval page (C2). The license holder opens `/approve?user_code=…` from
 * the AIS `/activate` redirect. The pending read goes through the generic service-admin proxy (any
 * signed-in role, session tenant); the approval is the role-agnostic strict write row, so the BFF
 * checks the two-factor code and the AIS checks that the signed-in user is the recorded approver.
 * Both calls read the body themselves and return the error code, so the page can say something
 * useful; neither throws on an HTTP answer.
 */

export interface PendingEnrollment {
  readonly user_code: string;
  readonly label: string | null;
  readonly platform: string | null;
  readonly key: string;
  readonly requested_clearance: string | null;
  readonly approver: { readonly sub: string; readonly display: string };
  readonly expires_at: string | number | null;
}

export interface ApprovedEnrollment {
  readonly status: "approved";
  readonly agent_id: string;
}

export type ApproveResult<T> =
  | { readonly ok: true; readonly body: T }
  | { readonly ok: false; readonly status: number; readonly code: string };

function codeOf(parsed: unknown, status: number): string {
  if (typeof parsed === "object" && parsed !== null) {
    const error = (parsed as { error?: unknown }).error;
    if (typeof error === "string") return error;
    if (typeof error === "object" && error !== null && typeof (error as { code?: unknown }).code === "string") {
      return (error as { code: string }).code;
    }
  }
  return `http_${status}`;
}

async function send<T>(url: string, init: RequestInit): Promise<ApproveResult<T>> {
  let response: Response;
  try {
    response = await fetch(url, { credentials: "same-origin", ...init });
  } catch {
    return { ok: false, status: 0, code: "network" };
  }
  const parsed: unknown = await response.json().catch(() => null);
  if (response.ok) return { ok: true, body: parsed as T };
  return { ok: false, status: response.status, code: codeOf(parsed, response.status) };
}

/** Read the pending enrollment for a user code. Never throws for an HTTP answer. */
export function fetchPendingEnrollment(userCode: string, signal?: AbortSignal): Promise<ApproveResult<PendingEnrollment>> {
  return send<PendingEnrollment>(
    `${API_BASE}/service-admin/ais/enrollments/pending?user_code=${encodeURIComponent(userCode)}`,
    { method: "GET", headers: { accept: "application/json" }, ...(signal === undefined ? {} : { signal }) },
  );
}

/** Approve the enrollment with a two-factor code. Never throws for an HTTP answer. */
export function approveEnrollment(userCode: string, key: string, mfaCode: string): Promise<ApproveResult<ApprovedEnrollment>> {
  // `key` is the anchor thumbprint shown on the page; the AIS refuses the approval if it changed.
  return send<ApprovedEnrollment>(`${API_BASE}/service-admin/ais/enrollments/approve`, {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify({ user_code: userCode, key, mfa_code: mfaCode }),
  });
}

/** Plain-language text for a refusal code. Anything unknown gets a generic line, never a raw code. */
export function approveMessage(code: string): string {
  switch (code) {
    case "approver_mismatch":
      return "Only the license holder named on this request can approve this agent. Sign in as that user to approve it.";
    case "mfa_required":
      return "Enter a current two-factor code to continue.";
    case "mfa_enrollment_required":
      return "Set up two-factor authentication on your account first. Approving an agent needs a second factor.";
    case "not_found":
      return "This approval request was not found. It may have expired, been approved already, or belong to another team.";
    case "enrollment_not_active":
      return "This enrollment is no longer waiting for approval. Ask the agent to start enrollment again.";
    case "key_mismatch":
      return "The agent's key changed after this page loaded, so it was not approved. Ask the agent to start enrollment again.";
    case "agent_suspended":
      return "This agent is suspended, so it cannot be approved.";
    case "service_unavailable":
    case "service_auth_failed":
    case "unavailable":
      return "The approval service is not available right now. Try again in a moment.";
    case "network":
      return "The request could not be sent. Check your connection and try again.";
    default:
      return "The agent could not be approved. Nothing was changed.";
  }
}
